<template>
  <div class="px-6 lg:px-10 py-6">
    <h1 class="text-[26px] font-bold text-gray-900 mb-12">Check Report</h1>

    <div v-if="!canView" class="mt-10"></div>

    <DynamicReportForm
      v-else
      v-model="model"
      :fields="fields"
      :columns="2"
      stackAt="md"
      contentWidth="2xl"
      actionLabel="View Report"
      :showTitle="false"
      :loading="loading"
      :errors="errors"
      @submit="onSubmit"
    />
  </div>
</template>

<script setup>
import { ref, watch, onMounted } from "vue"
import DynamicReportForm from "@/components/DynamicReportForm.vue"
import api from "@/api"
import Swal from "sweetalert2"
import { loadMyPermissions, hasAny } from "@/utils/permissions"

const canView = ref(false)
const loading = ref(false)
const errors  = ref({})

onMounted(async () => {
  await loadMyPermissions(false)
  canView.value = hasAny(["check_report.view"])
})

const model = ref({
  company_id:     "",
  expense:        "",
  expense_status: null, // تم تغيير القيمة الافتراضية إلى null
  from_date:      "",
  to_date:        "",
})

// field value (expense_status) -> API status value
const STATUS_MAP = {
  expensed:     "approved",
  not_expensed: "rejected",
  inactive:     "inactive",
}

const fields = [
  {
    name:        "company_id",
    label:       "Select Company",
    type:        "select",
    span:        "full",
    placeholder: "Select company",
    loader: async () => {
      try {
        const res = await api.get("/accounting/companies")
        return (res.data ?? []).map(c => ({
          label: c.name ?? c.company_name,
          value: c.id,
        }))
      } catch (e) {
        console.error("Error fetching companies:", e)
        return []
      }
    },
  },
  {
    name:        "expense",
    label:       "Select Expense",
    type:        "select",
    span:        "full",
    placeholder: "Select Expense",
    loader: async () => [
      { label: "All",         value: "all"         },
      { label: "Cash",        value: "cash"        },
      { label: "Credit Card", value: "credit_card" },
      { label: "Check",       value: "check"       },
    ],
  },
  {
    name:        "expense_status",
    label:       "Select Expense Status",
    type:        "select",
    span:        "full",
    placeholder: "Select Expense Status",
    loader: async () => [
      { label: "All",          value: null          },
      { label: "Expensed",     value: "expensed"    },
      { label: "Not Expensed", value: "not_expensed" },
      { label: "Inactive",     value: "inactive"    },
    ],
  },
  { name: "from_date", label: "From", type: "date", span: 1, placeholder: "yyyy/mm/dd" },
  { name: "to_date",   label: "To",   type: "date", span: 1, placeholder: "yyyy/mm/dd" },
]

// Clear a field's error as soon as the user fills it in
watch(model, (v) => {
  if (!Object.keys(errors.value).length) return
  const e = { ...errors.value }
  if (v.company_id) delete e.company_id
  if (v.from_date)  delete e.from_date
  if (v.to_date)    delete e.to_date
  if (v.from_date && v.to_date && v.from_date <= v.to_date) delete e.to_date
  errors.value = e
}, { deep: true })

function validate(payload) {
  const e = {}
  if (!payload.company_id) e.company_id = "Please select a company"
  if (!payload.from_date)  e.from_date  = "Please select a start date"
  if (!payload.to_date)    e.to_date    = "Please select an end date"
  if (payload.from_date && payload.to_date && payload.from_date > payload.to_date) {
    e.to_date = "End date must be after the start date"
  }
  errors.value = e
  return Object.keys(e).length === 0
}

async function onSubmit(payload) {
  if (!canView.value) return
  if (loading.value) return
  if (!validate(payload)) return

  const params = new URLSearchParams()
  params.append("company_id", payload.company_id)
  params.append("from_date",  payload.from_date)
  params.append("to_date",    payload.to_date)

  if (payload.expense && payload.expense !== "all") {
    params.append("payment_method", payload.expense)
  }

  if (payload.expense_status !== null && payload.expense_status !== undefined) {
    const status = STATUS_MAP[payload.expense_status]
    if (status) params.append("status", status)
  } else {
    // params.append("status", null)
  }

  loading.value = true
  try {
    const res = await api.get(`/accounting/checks/report/pdf?${params.toString()}`, {
      responseType: "blob",
    })
    const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }))
    window.open(url, "_blank")
  } catch (err) {
    console.error("Failed to generate report:", err)
    Swal.fire({
      icon: "error",
      title: "Failed to generate report",
      toast: true,
      position: "top",
      timer: 2000,
      showConfirmButton: false,
    })
  } finally {
    loading.value = false
  }
}
</script>
